"use client";

import * as React from "react";
import {
  BrainCircuit,
  Sparkles,
  ArrowLeft,
  Wallet,
  Bot,
  GraduationCap,
  Headphones,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { faNum } from "@/lib/format";
import { pushView } from "@/stores/useAppStore";

type Settings = {
  siteName?: string | null;
  supportPhone?: string | null;
  heroTitle?: string | null;
  heroSubtitle?: string | null;
  commissionPct?: number | null;
};

const DEFAULT_HERO_TITLE = "آینده را یاد بگیر، درآمد بساز";
const DEFAULT_HERO_SUBTITLE =
  "پلتفرم یکپارچه برای آموزش هوش مصنوعی، دسترسی به اکانت‌های رسمی و کسب درآمد از طریق سیستم کمیسیون — همه در یک جا.";

type Stat = {
  icon: LucideIcon;
  label: string;
  value: string;
};

export function Hero() {
  const [settings, setSettings] = React.useState<Settings | null>(null);

  React.useEffect(() => {
    let alive = true;
    fetch("/api/settings", { cache: "no-store" })
      .then((r) => r.json())
      .then((data: { settings?: Settings }) => {
        if (alive) setSettings(data.settings ?? {});
      })
      .catch(() => {
        if (alive) setSettings({});
      });
    return () => {
      alive = false;
    };
  }, []);

  const commissionPct = settings?.commissionPct ?? 10;
  const heroTitle = settings?.heroTitle?.trim() || DEFAULT_HERO_TITLE;
  const heroSubtitle =
    settings?.heroSubtitle?.trim() || DEFAULT_HERO_SUBTITLE;

  const STATS: Stat[] = [
    { icon: GraduationCap, label: "دوره‌های تعاملی", value: "استاندارد SCORM" },
    { icon: Bot, label: "اکانت رسمی", value: "هوش مصنوعی" },
    {
      icon: Wallet,
      label: "سیستم کمیسیون",
      value: `تا ${faNum(commissionPct)}٪`,
    },
    { icon: Headphones, label: "پشتیبانی فارسی", value: "۲۴/۷" },
  ];

  const useDefaultTitle = heroTitle === DEFAULT_HERO_TITLE;

  return (
    <section
      aria-labelledby="hero-heading"
      className="relative isolate overflow-hidden border-b border-white/5 bg-zinc-950 text-zinc-50"
    >
      {/* Background layers */}
      <div
        aria-hidden
        className="bg-grid-pattern absolute inset-0 -z-10 opacity-50"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-950/60 via-zinc-950 to-zinc-950"
      />
      <div
        aria-hidden
        className="absolute -top-32 right-1/4 -z-10 size-[40rem] rounded-full bg-primary/25 blur-3xl animate-pulse-glow"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-20 -z-10 size-[32rem] rounded-full bg-primary/10 blur-3xl"
      />

      <div className="mx-auto grid w-full max-w-7xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:py-28">
        {/* Text column */}
        <div className="flex flex-col items-start gap-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-primary" />
            </span>
            پلتفرم آموزش و درآمد هوش مصنوعی
          </span>

          <h1
            id="hero-heading"
            className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl md:text-6xl"
          >
            {useDefaultTitle ? (
              <>
                آینده را یاد بگیر،{" "}
                <span className="text-primary">درآمد بساز</span>
              </>
            ) : (
              heroTitle
            )}
          </h1>

          <p className="max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">
            {heroSubtitle}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              className="gap-2"
              onClick={() => pushView("academy")}
            >
              شروع یادگیری
              <ArrowLeft className="size-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="gap-2 border-white/20 bg-white/5 text-zinc-50 hover:bg-white/10 hover:text-zinc-50"
              onClick={() => pushView("commission")}
            >
              <Wallet className="size-4" />
              کسب درآمد
            </Button>
          </div>

          {/* Stat chips */}
          <dl className="mt-2 grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                  <s.icon className="size-4" />
                </span>
                <div className="flex flex-col leading-tight">
                  <dt className="text-[11px] text-zinc-400">{s.label}</dt>
                  <dd className="text-sm font-semibold text-zinc-100">
                    {s.value}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        </div>

        {/* Visual column */}
        <div className="relative hidden md:block">
          <div className="relative mx-auto aspect-square max-w-md">
            {/* glow */}
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-full bg-primary/25 blur-3xl animate-pulse-glow"
            />
            {/* ring */}
            <div
              aria-hidden
              className="absolute inset-0 rounded-full border border-primary/20"
            />
            <div
              aria-hidden
              className="absolute inset-6 rounded-full border border-dashed border-primary/15"
            />

            {/* main card */}
            <div className="absolute inset-1/4 flex flex-col items-center justify-center gap-3 rounded-3xl border border-primary/30 bg-zinc-900/80 p-6 text-center shadow-2xl backdrop-blur-md">
              <span className="flex size-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
                <BrainCircuit className="size-8" />
              </span>
              <p className="text-sm font-semibold">یاد بگیر، بساز، درآمد کسب کن</p>
              <p className="text-xs text-zinc-400">آکادمی · اکانت · کمیسیون</p>
            </div>

            {/* floating chips */}
            <div className="absolute -right-4 top-6 flex items-center gap-2 rounded-xl border border-white/10 bg-zinc-900/80 px-3 py-2 shadow-lg backdrop-blur-sm">
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary/20 text-primary">
                <Sparkles className="size-4" />
              </span>
              <div className="text-[11px] leading-tight">
                <p className="font-semibold text-zinc-100">دوره‌های تعاملی</p>
                <p className="text-zinc-400">پیشرفت خودکار</p>
              </div>
            </div>

            <div className="absolute -left-4 bottom-10 flex items-center gap-2 rounded-xl border border-white/10 bg-zinc-900/80 px-3 py-2 shadow-lg backdrop-blur-sm">
              <span className="flex size-8 items-center justify-center rounded-lg bg-primary/20 text-primary">
                <Bot className="size-4" />
              </span>
              <div className="text-[11px] leading-tight">
                <p className="font-semibold text-zinc-100">اکانت‌های رسمی</p>
                <p className="text-zinc-400">ChatGPT · Claude · Midjourney</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
